/**
 * Zod schemas for suite postMessage payloads.
 *
 * All new fields MUST be `.optional()` — existing compiled suite apps
 * do not auto-update, and adding a required field would break them.
 * If you need a hard requirement, do it in two steps: optional first,
 * then tighten only after every shipped suite app has been rebuilt.
 */

import { z } from 'zod';

export const InjectStudioSchema = z.object({
  seed: z.string().optional(),
  blends: z.array(z.string()).optional(),
  mood: z.array(z.string()).optional(),
  stylePreset: z.string().optional(),
  vocalPackKey: z.string().optional(),
  selectedVoiceSpec: z.enum(['Lead', 'AltA', 'AltB']).optional(),
  includeHarmonyStack: z.boolean().optional(),
  includeGangVocals: z.boolean().optional(),
  explicit: z.boolean().optional(),
  povSetting: z.string().optional(),
  themesToHit: z.string().optional(),
  themesToAvoid: z.string().optional(),
  instruments: z.string().optional(),
  constraints: z.string().optional(),
  styleStrength: z.number().optional(),
  weirdness: z.number().optional(),
}).strict();

export const SaveDraftSchema = z.object({
  title: z.string().min(1, 'title required'),
  artist: z.string().optional(),
  style: z.string().optional(),
  excludedStyle: z.string().optional(),
  lyricsWithTags: z.string().optional(),
  sourceType: z.string().optional(),
  tags: z.string().optional(),
}).strict();

export const InjectSeedSchema = z.object({
  title: z.string().min(1, 'title required'),
  concept: z.string().min(1, 'concept required'),
  povSetting: z.string().optional(),
  mustHaves: z.array(z.string()).optional(),
  avoid: z.array(z.string()).optional(),
  instruments: z.array(z.string()).optional(),
  bpm: z.number().optional(),
  key: z.string().optional(),
}).strict();

export const PersonaIdentitySchema = z.object({
  source: z.enum(['crossroads', 'band-persona-architect']),
  bandBible: z.record(z.string(), z.unknown()).optional(),
  markdown: z.string().optional(),
  name: z.string().min(1, 'name required'),
  genre: z.string().optional(),
}).strict();

/** get-context has no payload — schema is permissive */
export const GetContextSchema = z.unknown();

export const PAYLOAD_SCHEMAS = {
  'suite:inject-studio': InjectStudioSchema,
  'suite:save-draft': SaveDraftSchema,
  'suite:inject-seed': InjectSeedSchema,
  'suite:get-context': GetContextSchema,
  'suite:send-persona-identity': PersonaIdentitySchema,
} as const;
